import React, { useState, useEffect, useCallback } from 'react';
import {
  FiPhone,
  FiMail,
  FiSearch,
  FiPlus,
  FiTrash2,
  FiEdit2,
  FiShare2,
  FiDownload,
  FiUser,
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import apiService from '../services/api';
import './Contacts.css';

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formData, setFormData] = useState({
    fullName: '',
    jobTitle: '',
    businessName: '',
    email: '',
    phoneNumber: '',
    website: '',
    address: '',
  });

  const loadContacts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getContacts();
      if (response.ok) {
        setContacts(response.contacts.map(enrichContact));
      } else {
        setContacts([]);
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  const enrichContact = (contact) => ({
    id: contact.id,
    name: contact.fullName || contact.name || 'Unnamed Contact',
    title: contact.jobTitle || contact.title || 'Professional',
    company: contact.businessName || contact.company || 'Company',
    email: contact.email || '',
    phone: contact.phoneNumber || contact.phone || '',
    website: contact.website || '',
    address: contact.address || '',
    notes: contact.notes || '',
    socialLinks: contact.socialLinks || null,
    isOmniUser: !!contact.linkedOmniUserId || !!contact.isOmniUser,
    source: contact.source || 'manual',
  });

  const resetForm = () => {
    setFormData({
      fullName: '',
      jobTitle: '',
      businessName: '',
      email: '',
      phoneNumber: '',
      website: '',
      address: '',
    });
    setEditingContact(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const openEditModal = (contact) => {
    setEditingContact(contact);
    setFormData({
      fullName: contact.name,
      jobTitle: contact.title,
      businessName: contact.company,
      email: contact.email,
      phoneNumber: contact.phone,
      website: contact.website,
      address: contact.address,
    });
    setShowAddModal(true);
  };

  const handleSaveContact = async () => {
    try {
      const payload = {
        fullName: formData.fullName,
        jobTitle: formData.jobTitle,
        businessName: formData.businessName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        website: formData.website,
        address: formData.address,
      };

      if (editingContact) {
        await apiService.updateContact(editingContact.id, payload);
      } else {
        await apiService.createContact(payload);
      }

      setShowAddModal(false);
      resetForm();
      loadContacts();
    } catch (error) {
      console.error('Error saving contact:', error);
      alert('Failed to save contact. Please try again.');
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      try {
        await apiService.deleteContact(contactId);
        loadContacts();
      } catch (error) {
        console.error('Error deleting contact:', error);
        alert('Failed to delete contact.');
      }
    }
  };

  const handleCall = (phone) => {
    if (!phone) return alert('No phone number available.');
    window.open(`tel:${phone.replace(/[^0-9+]/g, '')}`);
  };

  const handleEmail = (email) => {
    if (!email) return alert('No email address available.');
    window.open(`mailto:${email}`);
  };

  const handleWhatsApp = (phone) => {
    if (!phone) return alert('No phone number available.');
    const phoneNumber = phone.replace(/[^0-9+]/g, '');
    window.open(`https://wa.me/${phoneNumber}`, '_blank');
  };

  const generateVCF = (contact) => {
    const vcfData = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${contact.name}`,
      contact.title ? `TITLE:${contact.title}` : '',
      contact.company ? `ORG:${contact.company}` : '',
      contact.email ? `EMAIL:${contact.email}` : '',
      contact.phone ? `TEL:${contact.phone}` : '',
      contact.website ? `URL:${contact.website}` : '',
      contact.address ? `ADR:;;${contact.address};;;;` : '',
      'END:VCARD',
    ].filter(Boolean).join('\n');

    const blob = new Blob([vcfData], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${contact.name.replace(/\s+/g, '_')}.vcf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredContacts = contacts.filter(
    (contact) =>
      contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getInitials = (name) => {
    return name
      .split(' ')
      .filter(Boolean)
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  const getBadgeLabel = (contact) => {
    if (contact.isOmniUser) return 'OMNI';
    if (contact.source === 'card') return 'Parsed';
    return 'Manual';
  };

  return (
    <div className="contacts-container">
      <div className="contacts-content">
        {/* Header */}
        <div className="contacts-header">
          <h1 className="contacts-title">My Contacts</h1>
        </div>

        {/* Search and Add */}
        <div className="contacts-actions">
          <div className="search-box">
            <FiSearch size={20} />
            <input
              type="text"
              placeholder="Search contacts..."
              className="search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button className="add-contact-btn" onClick={openAddModal}>
            <FiPlus size={24} />
          </button>
        </div>

        {/* Contacts List */}
        {loading ? (
          <div className="loading-state">
            <div className="spinner" />
            <span>Loading contacts...</span>
          </div>
        ) : filteredContacts.length === 0 ? (
          <div className="empty-state">
            <FiUser size={64} />
            <h3>No Contacts Yet</h3>
            <p>{searchQuery ? 'No contacts match your search' : 'Add your first contact to get started'}</p>
            {!searchQuery && (
              <button className="btn-primary" onClick={openAddModal}>
                <FiPlus size={20} />
                Add Contact
              </button>
            )}
          </div>
        ) : (
          <div className="contacts-list">
            {filteredContacts.map((contact) => (
              <div key={contact.id} className="contact-card">
                <div className="contact-card-top">
                  <div className="contact-avatar-column">
                    <div className="contact-avatar">
                      <span>{getInitials(contact.name)}</span>
                    </div>
                    <div className="contact-badge">
                      <span>{getBadgeLabel(contact)}</span>
                    </div>
                  </div>
                  <div className="contact-info">
                    <h3 className="contact-name">{contact.name}</h3>
                    <p className="contact-title">{contact.title}</p>
                    <p className="contact-company">{contact.company}</p>
                    {contact.website && <p className="contact-website">{contact.website}</p>}
                    {contact.socialLinks && Object.keys(contact.socialLinks).length > 0 && (
                      <div className="social-indicator">
                        <FiShare2 size={12} />
                        <span>
                          {Object.keys(contact.socialLinks).length} social link
                          {Object.keys(contact.socialLinks).length > 1 ? 's' : ''}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="contact-actions-row">
                  <button className="contact-action-btn" onClick={() => handleCall(contact.phone)} title="Call">
                    <FiPhone size={18} />
                  </button>
                  <button className="contact-action-btn" onClick={() => handleWhatsApp(contact.phone)} title="WhatsApp">
                    <FaWhatsapp size={18} />
                  </button>
                  <button className="contact-action-btn" onClick={() => handleEmail(contact.email)} title="Email">
                    <FiMail size={18} />
                  </button>
                  <button className="contact-action-btn" onClick={() => openEditModal(contact)} title="Edit">
                    <FiEdit2 size={18} />
                  </button>
                  <button className="contact-action-btn" onClick={() => generateVCF(contact)} title="Download VCF">
                    <FiDownload size={18} />
                  </button>
                  <button className="contact-action-btn delete" onClick={() => handleDeleteContact(contact.id)} title="Delete">
                    <FiTrash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Stats */}
        <div className="stats-container">
          <div className="stat-item">
            <span className="stat-number">{contacts.length}</span>
            <span className="stat-label">Total Contacts</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{contacts.filter((c) => c.source === 'card').length}</span>
            <span className="stat-label">From Cards</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{contacts.filter((c) => c.isOmniUser).length}</span>
            <span className="stat-label">OMNI Users</span>
          </div>
        </div>
      </div>

      {/* Add/Edit Contact Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingContact ? 'Edit Contact' : 'Add New Contact'}</h2>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    placeholder="John Doe"
                  />
                </div>

                <div className="form-group">
                  <label>Job Title</label>
                  <input
                    type="text"
                    value={formData.jobTitle}
                    onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                    placeholder="CEO"
                  />
                </div>

                <div className="form-group">
                  <label>Company</label>
                  <input
                    type="text"
                    value={formData.businessName}
                    onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                    placeholder="Company Inc."
                  />
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com"
                  />
                </div>

                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                    placeholder="+1 234 567 8900"
                  />
                </div>

                <div className="form-group">
                  <label>Website</label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://example.com"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Address</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="123 Main St, City, Country"
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleSaveContact} disabled={!formData.fullName}>
                {editingContact ? 'Update Contact' : 'Add Contact'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contacts;
